import { useEffect, useRef } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { apiFetch, AuthError } from "./client";
import { queryKeys, useAuthenticatedClient } from "./queries";
import type { components } from "./schema";
import {
  applyMutationToShell,
  errorMessage,
  mergeFields,
  mergeIssueDelta,
  refreshOperationalViews,
  type V2RequestErrorBody,
} from "@/features/dropship/cache";
import { replaceLineInPages } from "@/features/dropship/projections";
import type {
  DropshipV2Field,
  DropshipV2Issue,
  DropshipV2Line,
  DropshipV2LinePage,
  DropshipV2Shell,
  DropshipV2LineCreate,
} from "@/features/dropship/types";

export type {
  DropshipV2Field,
  DropshipV2Issue,
  DropshipV2Line,
  DropshipV2LineChanges,
  DropshipV2LineCreate,
  DropshipV2LineGroup,
  DropshipV2LinePage,
  DropshipV2Mutation,
  DropshipV2Shell,
} from "@/features/dropship/types";
export {
  optimisticallyPatchLineInPages,
  restoreLinesInPages,
} from "@/features/dropship/projections";
export {
  useCreateDropshipV2Line,
  useCreateDropshipV2ScratchLine,
  usePatchDropshipV2Line,
  useRemoveDropshipV2Line,
} from "@/features/dropship/line-mutations";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:80";
const PROCESSING_STAGES = new Set([
  "queued",
  "decoding",
  "parsing",
  "normalizing",
  "validating",
]);
const ACTIVE_CLARIFICATION_STATES = new Set(["queued", "running", "retrying"]);

const aiBuildKey = (draftUuid: string) =>
  ["dropship-v2", "ai-build", draftUuid] as const;
const aiConversationKey = (draftUuid: string, lineUuid?: string | null) =>
  ["dropship-v2", "ai-conversation", draftUuid, lineUuid ?? "main"] as const;

function idempotencyKey(prefix: string) {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
}

export type DropshipV2Capabilities = {
  intake_enabled: boolean;
  approvals_enabled: boolean;
  placement_enabled: boolean;
  can_sales_approve: boolean;
  can_pm_approve: boolean;
  can_place: boolean;
};
export type DropshipV2FreightReview =
  components["schemas"]["DropshipV2FreightReviewRead"];
export type DropshipV2FreightReviewGroup =
  components["schemas"]["DropshipV2FreightReviewGroupRead"];
export type DropshipFreightPricingGroup =
  components["schemas"]["DropshipFreightPricingGroupRead"];
export type DropshipV2Plan = components["schemas"]["DropshipV2POPlanRead"];
export type DropshipV2ProposedPO =
  components["schemas"]["DropshipV2ProposedPORead"];
export type DropshipV2ProposedPOLine = DropshipV2ProposedPO["lines"][number];
export type DropshipV2Placement =
  components["schemas"]["DropshipV2PlacementRead"];
export type DropshipV2AuditEvent =
  components["schemas"]["DropshipV2AuditEventRead"];
export type DropshipV2WorkQueue =
  components["schemas"]["DropshipV2WorkQueueRead"];
export type DropshipV2WorkQueueItem =
  components["schemas"]["DropshipV2WorkQueueItemRead"];
export type DropshipV2ReadinessMetrics =
  components["schemas"]["DropshipV2ReadinessMetricsRead"];
/** @deprecated Use DropshipV2LineCreate. AQProj and scratch drafts share the route. */
export type DropshipV2ScratchLine = DropshipV2LineCreate;
export type DropshipV2ProposedPOChanges = Omit<
  components["schemas"]["DropshipV2ProposedPODecisionsUpdate"],
  "expected_revision"
>;

export type DropshipV2AIBuild = components["schemas"]["DropshipV2AIBuildRead"];
export type DropshipV2AILineAgent =
  components["schemas"]["DropshipV2LineAgentStateRead"];
export type DropshipV2AIProductMatch =
  components["schemas"]["DropshipV2AIProductMatchRead"];
export type DropshipV2AIConversation =
  components["schemas"]["DropshipV2ContextualConversationRead"];
export type DropshipV2AIAlternativeApply =
  components["schemas"]["DropshipV2AlternativeApplyRead"];
export type DropshipV2ClarificationQuestion =
  components["schemas"]["DropshipV2ClarificationQuestionRead"];
export type DropshipV2Clarification = DropshipV2ClarificationQuestion;
export type DropshipV2ClarificationList =
  components["schemas"]["DropshipV2ClarificationListRead"];
export type DropshipV2Clarifications = DropshipV2ClarificationList;
export type DropshipV2ClarificationAnswer =
  components["schemas"]["DropshipV2ClarificationAnswerInput"];
export type DropshipV2ClarificationAnswerInput = DropshipV2ClarificationAnswer;
export type DropshipV2ClarificationAnswersRequest =
  components["schemas"]["DropshipV2ClarificationAnswersRequest"];
export type DropshipV2ClarificationAnswers =
  components["schemas"]["DropshipV2ClarificationAnswersRead"];

export type DropshipV2WorkQueueParams = {
  task_state?: string;
  task_type?: string;
  workflow_state?: string;
  owner_id?: number;
  unassigned?: boolean;
  min_age_hours?: number;
  search?: string;
  supplier?: string;
  blocking_reason?: string;
  limit?: number;
  offset?: number;
};

export class DropshipV2RequestError extends Error {
  status: number;
  code?: string;

  constructor(status: number, body: unknown, fallback: string) {
    const typedBody = body as V2RequestErrorBody | undefined;
    const detail = typedBody?.detail;
    const message =
      typeof detail === "string"
        ? detail
        : detail?.message || detail?.code?.replaceAll("_", " ") || fallback;
    super(message);
    this.name = "DropshipV2RequestError";
    this.status = status;
    this.code = typeof detail === "object" ? detail?.code : undefined;
  }
}

function updatePlanPO(
  plan: DropshipV2Plan | undefined,
  proposedPO: DropshipV2ProposedPO,
): DropshipV2Plan | undefined {
  if (!plan || plan.id !== proposedPO.plan_id) return plan;
  return {
    ...plan,
    proposed_pos: plan.proposed_pos.map((item) =>
      item.uuid === proposedPO.uuid ? proposedPO : item,
    ),
  };
}

function proposedPOMutationScope(draftUuid: string) {
  return { id: `dropship-v2-proposed-po:${draftUuid}` };
}

function restorePlanPOAtRevision(
  plan: DropshipV2Plan | undefined,
  previousPO: DropshipV2ProposedPO | undefined,
  optimisticPO: DropshipV2ProposedPO | undefined,
  optimisticRevision: number | undefined,
) {
  if (!plan || !previousPO || !optimisticPO || optimisticRevision == null)
    return plan;
  const currentPO = plan.proposed_pos.find(
    (candidate) => candidate.uuid === previousPO.uuid,
  );
  return currentPO === optimisticPO && currentPO.revision === optimisticRevision
    ? updatePlanPO(plan, previousPO)
    : plan;
}

function refreshDraftViews(
  queryClient: QueryClient,
  draftUuid: string,
  includeClarifications = true,
) {
  void Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.dropshipV2.shell(draftUuid),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.dropshipV2.fields(draftUuid),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.dropshipV2.lineGroups(draftUuid),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.dropshipV2.issues(draftUuid),
    }),
    ...(includeClarifications
      ? [
          queryClient.invalidateQueries({
            queryKey: queryKeys.dropshipV2.clarifications(draftUuid),
          }),
        ]
      : []),
    queryClient.invalidateQueries({
      queryKey: queryKeys.dropshipV2.freightReview(draftUuid),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.dropshipV2.deliveryTiming(draftUuid),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.dropshipQuoteDrafts.all,
    }),
  ]);
}

function refreshApprovalViews(
  queryClient: QueryClient,
  draftUuid: string,
  planId?: number,
) {
  void Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.dropshipV2.shell(draftUuid),
      exact: true,
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.dropshipV2.plan(draftUuid, planId),
      exact: planId != null,
    }),
  ]);
}

async function draftAIRequest<T>(
  getAccessToken: () => Promise<string | null>,
  path: string,
  init?: RequestInit,
  allowNotFound = false,
): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new AuthError(401, "Failed to acquire access token");
  const response = await apiFetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...init?.headers,
    },
  });
  const body: unknown = await response.json().catch(() => null);
  if (allowNotFound && response.status === 404) return null as T;
  if (!response.ok) {
    throw new DropshipV2RequestError(
      response.status,
      body,
      "Quote agent request failed",
    );
  }
  return body as T;
}

function draftAIPath(draftUuid: string, suffix: string) {
  return `/dashboard/dropship-intake/v2/drafts/${encodeURIComponent(draftUuid)}/${suffix}`;
}

function timestamp(value: string | null | undefined) {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
}

function compareOperationRevision(
  left: DropshipV2AIBuild["operation_revision"],
  right: DropshipV2AIBuild["operation_revision"],
) {
  for (let index = 0; index < left.length; index += 1) {
    const difference = left[index]! - right[index]!;
    if (difference !== 0) return difference;
  }
  return 0;
}

function mergeAIBuildLineAgents(
  current: DropshipV2AILineAgent[],
  incoming: DropshipV2AILineAgent[],
) {
  const currentByLine = new Map(current.map((line) => [line.line_uuid, line]));
  const incomingLines = new Set(incoming.map((line) => line.line_uuid));
  const merged = incoming.map((line) => {
    const previous = currentByLine.get(line.line_uuid);
    if (!previous) return line;
    const revisionDifference =
      (line.line_revision ?? -1) - (previous.line_revision ?? -1);
    if (revisionDifference > 0) return line;
    if (revisionDifference < 0) return previous;
    if (timestamp(line.updated_at) < timestamp(previous.updated_at))
      return previous;

    const previousProgress = previous.progress_percent;
    if (
      previousProgress == null ||
      (line.progress_percent ?? -1) >= previousProgress
    )
      return line;
    return { ...line, progress_percent: previousProgress };
  });
  return [
    ...merged,
    ...current.filter((line) => !incomingLines.has(line.line_uuid)),
  ];
}

function mergeAIBuildSnapshots(
  current: DropshipV2AIBuild | null | undefined,
  incoming: DropshipV2AIBuild | null,
): DropshipV2AIBuild | null {
  if (!incoming) return current ?? null;
  if (!current) return incoming;

  if (current.uuid !== incoming.uuid) {
    const createdDifference =
      timestamp(incoming.created_at) - timestamp(current.created_at);
    return createdDifference > 0 ? incoming : current;
  }

  const operationDifference = compareOperationRevision(
    incoming.operation_revision,
    current.operation_revision,
  );
  const mergedLines = mergeAIBuildLineAgents(
    current.line_agents,
    incoming.line_agents,
  );
  if (operationDifference < 0) return { ...current, line_agents: mergedLines };
  if (operationDifference > 0) return { ...incoming, line_agents: mergedLines };

  const latest =
    timestamp(incoming.updated_at) < timestamp(current.updated_at)
      ? current
      : incoming;

  return {
    ...latest,
    progress_percent: Math.max(
      current.progress_percent,
      incoming.progress_percent,
    ),
    progress: Math.max(current.progress, incoming.progress),
    line_agents: mergedLines,
  };
}

function setAIBuild(
  queryClient: QueryClient,
  draftUuid: string,
  build: DropshipV2AIBuild,
) {
  queryClient.setQueryData<DropshipV2AIBuild | null>(
    aiBuildKey(draftUuid),
    (current) => mergeAIBuildSnapshots(current, build),
  );
}

export function useDropshipV2AILatestBuild(draftUuid: string, enabled = true) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const previousTransition = useRef("");
  const previousContentRevision = useRef<number | null>(null);
  const previousTerminal = useRef<boolean | null>(null);
  const query = useQuery<DropshipV2AIBuild | null>({
    queryKey: aiBuildKey(draftUuid),
    queryFn: ({ signal }) =>
      draftAIRequest<DropshipV2AIBuild | null>(
        getAccessToken,
        draftAIPath(draftUuid, "ai-builds/latest"),
        { signal },
        true,
      ),
    enabled: enabled && Boolean(draftUuid),
    refetchInterval: (current) =>
      current.state.data?.terminal === false ? 1_000 : false,
    refetchIntervalInBackground: true,
    structuralSharing: (current, incoming) =>
      mergeAIBuildSnapshots(
        current as DropshipV2AIBuild | null | undefined,
        incoming as DropshipV2AIBuild | null,
      ),
  });
  const transition = query.data
    ? `${query.data.uuid}:${query.data.operation_revision.join(".")}:${query.data.status}:${query.data.stage}:${query.data.content_revision}`
    : "";

  useEffect(() => {
    if (!transition || previousTransition.current === transition) return;
    const initialized = previousTransition.current !== "";
    const refreshRequired =
      initialized &&
      (previousContentRevision.current !== query.data?.content_revision ||
        (previousTerminal.current === false && query.data?.terminal === true));
    previousTransition.current = transition;
    previousContentRevision.current = query.data?.content_revision ?? null;
    previousTerminal.current = query.data?.terminal ?? null;
    if (!refreshRequired) return;
    refreshDraftViews(queryClient, draftUuid);
  }, [draftUuid, query.data, queryClient, transition]);

  return query;
}

export function useUploadDropshipV2AIQuote(draftUuid: string) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const uploadKeys = useRef(new WeakMap<File, Map<number, string>>());
  return useMutation({
    mutationFn: ({
      file,
      expectedContentRevision,
    }: {
      file: File;
      expectedContentRevision: number;
    }) => {
      let keysByRevision = uploadKeys.current.get(file);
      if (!keysByRevision) {
        keysByRevision = new Map();
        uploadKeys.current.set(file, keysByRevision);
      }
      let uploadKey = keysByRevision.get(expectedContentRevision);
      if (!uploadKey) {
        uploadKey = idempotencyKey("ai-quote");
        keysByRevision.set(expectedContentRevision, uploadKey);
      }
      const form = new FormData();
      form.append("file", file);
      form.append("expected_content_revision", String(expectedContentRevision));
      form.append("idempotency_key", uploadKey);
      return draftAIRequest<DropshipV2AIBuild>(
        getAccessToken,
        draftAIPath(draftUuid, "ai-builds"),
        {
          method: "POST",
          body: form,
        },
      );
    },
    onSuccess: (build) => {
      setAIBuild(queryClient, draftUuid, build);
      refreshDraftViews(queryClient, draftUuid);
    },
    onError: (error) => {
      if (
        error instanceof DropshipV2RequestError &&
        [409, 412].includes(error.status)
      ) {
        void queryClient.invalidateQueries({
          queryKey: aiBuildKey(draftUuid),
          exact: true,
        });
        refreshDraftViews(queryClient, draftUuid);
      }
    },
  });
}

export function useDropshipV2AIConversation(
  draftUuid: string,
  lineUuid?: string | null,
  enabled = true,
) {
  const { getAccessToken } = useAuth();
  const scope = lineUuid
    ? `ai-conversations/lines/${encodeURIComponent(lineUuid)}`
    : "ai-conversations/main";
  return useQuery({
    queryKey: aiConversationKey(draftUuid, lineUuid),
    queryFn: ({ signal }) =>
      draftAIRequest<DropshipV2AIConversation | null>(
        getAccessToken,
        draftAIPath(draftUuid, scope),
        { signal },
        true,
      ),
    enabled: enabled && Boolean(draftUuid),
  });
}

export function useCreateDropshipV2AIConversation(draftUuid: string) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ lineUuid }: { lineUuid?: string | null }) => {
      const scope = lineUuid
        ? `ai-conversations/lines/${encodeURIComponent(lineUuid)}`
        : "ai-conversations/main";
      return draftAIRequest<DropshipV2AIConversation>(
        getAccessToken,
        draftAIPath(draftUuid, scope),
        { method: "POST" },
      );
    },
    onMutate: ({ lineUuid }) =>
      queryClient.cancelQueries({
        queryKey: aiConversationKey(draftUuid, lineUuid),
        exact: true,
      }),
    onSuccess: (conversation, variables) => {
      queryClient.setQueryData(
        aiConversationKey(draftUuid, variables.lineUuid),
        conversation,
      );
    },
  });
}

export function useApplyDropshipV2AIAlternative(draftUuid: string) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const lineKey = queryKeys.dropshipV2.lineGroups(draftUuid);
  const fieldKey = queryKeys.dropshipV2.fields(draftUuid);
  const issueKey = queryKeys.dropshipV2.issues(draftUuid);
  return useMutation({
    mutationFn: ({
      buildUuid,
      lineUuid,
      alternative,
      expectedRevision,
    }: {
      buildUuid: string;
      lineUuid: string;
      alternative: DropshipV2AIProductMatch;
      expectedRevision: number;
    }) =>
      draftAIRequest<DropshipV2AIAlternativeApply>(
        getAccessToken,
        draftAIPath(
          draftUuid,
          `ai-builds/${encodeURIComponent(buildUuid)}/lines/${encodeURIComponent(lineUuid)}/alternatives/${encodeURIComponent(alternative.uuid)}/apply`,
        ),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            expected_revision: expectedRevision,
            product_uuid: alternative.product_uuid,
            product_id: alternative.product_id ?? undefined,
          }),
        },
      ),
    onSuccess: async (result) => {
      await queryClient.cancelQueries({
        queryKey: aiBuildKey(draftUuid),
        exact: true,
      });
      setAIBuild(queryClient, draftUuid, result.build);
      if (result.mutation.line) {
        queryClient.setQueryData<InfiniteData<DropshipV2LinePage>>(
          lineKey,
          (current) =>
            replaceLineInPages(current, result.mutation.line as DropshipV2Line),
        );
      }
      for (const relatedLine of result.mutation.related_lines) {
        queryClient.setQueryData<InfiniteData<DropshipV2LinePage>>(
          lineKey,
          (current) => replaceLineInPages(current, relatedLine),
        );
      }
      queryClient.setQueryData<DropshipV2Field[]>(fieldKey, (current) =>
        mergeFields(current, result.mutation.related_fields),
      );
      queryClient.setQueryData<DropshipV2Issue[]>(issueKey, (current) =>
        mergeIssueDelta(current, result.mutation.issue_delta),
      );
      await applyMutationToShell(queryClient, draftUuid, result.mutation);
      refreshOperationalViews(queryClient, draftUuid);
      refreshDraftViews(queryClient, draftUuid);
    },
    onError: (error) => {
      if (
        error instanceof DropshipV2RequestError &&
        [409, 412].includes(error.status)
      ) {
        void queryClient.invalidateQueries({ queryKey: aiBuildKey(draftUuid) });
        refreshDraftViews(queryClient, draftUuid);
      }
    },
  });
}

export function useDropshipV2Capabilities() {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.dropshipV2.capabilities,
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/dropship-intake/v2/capabilities",
      );
      if (error) throw error;
      return data as DropshipV2Capabilities;
    },
    staleTime: 5 * 60_000,
  });
}

export function useDropshipV2WorkQueue(
  params: DropshipV2WorkQueueParams = {},
  enabled = true,
) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.dropshipV2.workQueue(params),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/dropship-intake/v2/work-queue",
        {
          params: { query: params },
        },
      );
      if (error) throw error;
      return data;
    },
    enabled,
  });
}

export function useDropshipV2ReadinessMetrics(windowDays = 30, enabled = true) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.dropshipV2.readinessMetrics(windowDays),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/dropship-intake/v2/readiness-metrics",
        {
          params: { query: { window_days: windowDays } },
        },
      );
      if (error) throw error;
      return data;
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useDropshipV2Audit(draftUuid: string, enabled = true) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.dropshipV2.audit(draftUuid),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/audit",
        {
          params: {
            path: { draft_uuid: draftUuid },
            query: { limit: 100, offset: 0 },
          },
        },
      );
      if (error) throw error;
      return data;
    },
    enabled: enabled && !!draftUuid,
  });
}

export function useCreateDropshipV2Draft() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      const form = new FormData();
      form.append("file", file);
      const response = await fetch(
        `${API_BASE_URL}/dashboard/dropship-intake/v2/drafts`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        },
      );
      const body: unknown = await response.json().catch(() => null);
      if (!response.ok)
        throw new DropshipV2RequestError(
          response.status,
          body,
          "AQProj upload failed",
        );
      return body as DropshipV2Shell;
    },
    onSuccess: (shell) => {
      queryClient.setQueryData(queryKeys.dropshipV2.shell(shell.uuid), shell);
      queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipQuoteDrafts.all,
      });
      refreshOperationalViews(queryClient, shell.uuid);
    },
  });
}

export function useCreateDropshipV2ScratchDraft() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectName,
      draftNotes,
    }: {
      projectName: string;
      draftNotes?: string;
    }) => {
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      const form = new URLSearchParams({ project_name: projectName });
      if (draftNotes) form.set("draft_notes", draftNotes);
      const response = await fetch(
        `${API_BASE_URL}/dashboard/dropship-intake/v2/drafts/from-scratch`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: form,
        },
      );
      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        throw new DropshipV2RequestError(
          response.status,
          body,
          "Scratch draft creation failed",
        );
      }
      return body as DropshipV2Shell;
    },
    onSuccess: (shell) => {
      queryClient.setQueryData(queryKeys.dropshipV2.shell(shell.uuid), shell);
      queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipQuoteDrafts.all,
      });
      refreshOperationalViews(queryClient, shell.uuid);
    },
  });
}

export function useDropshipV2Shell(draftUuid: string, enabled = true) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.dropshipV2.shell(draftUuid),
    queryFn: async ({ signal }) => {
      const client = await getClient();
      const { data, error, response } = await client.GET(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/shell",
        { params: { path: { draft_uuid: draftUuid } }, signal },
      );
      if (error) {
        throw new DropshipV2RequestError(
          response.status,
          error,
          "Unable to load the draft",
        );
      }
      return data;
    },
    enabled: enabled && !!draftUuid,
    retry: (failureCount, error) =>
      error instanceof DropshipV2RequestError && error.status === 404
        ? false
        : failureCount < 2,
    refetchInterval: (query) =>
      PROCESSING_STAGES.has(query.state.data?.processing_stage ?? "")
        ? 1_000
        : false,
  });
}

export function useDropshipV2Fields(draftUuid: string, enabled = true) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.dropshipV2.fields(draftUuid),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/fields",
        { params: { path: { draft_uuid: draftUuid } } },
      );
      if (error) throw error;
      return data;
    },
    enabled: enabled && !!draftUuid,
  });
}

export function useDropshipV2LineGroups(draftUuid: string, enabled = true) {
  const getClient = useAuthenticatedClient();
  return useInfiniteQuery({
    queryKey: queryKeys.dropshipV2.lineGroups(draftUuid),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam, signal }) => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/line-groups",
        {
          params: {
            path: { draft_uuid: draftUuid },
            query: { cursor: pageParam, limit: 40 },
          },
          signal,
        },
      );
      if (error) throw error;
      return data;
    },
    getNextPageParam: (page) => (page.has_more ? page.next_cursor : undefined),
    enabled: enabled && !!draftUuid,
    refetchOnWindowFocus: false,
  });
}

export function useDropshipV2Issues(draftUuid: string, enabled = true) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.dropshipV2.issues(draftUuid),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/issues",
        {
          params: {
            path: { draft_uuid: draftUuid },
            query: { include_resolved: false },
          },
        },
      );
      if (error) throw error;
      return data;
    },
    enabled: enabled && !!draftUuid,
  });
}

export function useDropshipV2Clarifications(draftUuid: string, enabled = true) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const previousTransition = useRef("");
  const previousContentRevision = useRef<number | null>(null);
  const query = useQuery({
    queryKey: queryKeys.dropshipV2.clarifications(draftUuid),
    queryFn: async ({ signal }) => {
      const client = await getClient();
      const { data, error, response } = await client.GET(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/clarifications",
        {
          params: { path: { draft_uuid: draftUuid } },
          signal,
        },
      );
      if (error) {
        throw new DropshipV2RequestError(
          response.status,
          error,
          "Unable to load quote clarifications",
        );
      }
      return data;
    },
    enabled: enabled && Boolean(draftUuid),
    refetchInterval: (query) =>
      query.state.data?.questions.some(
        (question) =>
          question.status === "open" &&
          ACTIVE_CLARIFICATION_STATES.has(question.workflow_state),
      )
        ? 1_000
        : false,
    refetchIntervalInBackground: true,
  });
  const transition = query.data
    ? `${query.data.content_revision}:${query.data.questions
        .map(
          (question) =>
            `${question.uuid}:${question.status}:${question.workflow_state}:${question.updated_at}`,
        )
        .join("|")}`
    : "";

  useEffect(() => {
    if (!transition || previousTransition.current === transition) return;
    const initialized = previousTransition.current !== "";
    const refreshRequired =
      initialized &&
      previousContentRevision.current !== query.data?.content_revision;
    previousTransition.current = transition;
    previousContentRevision.current = query.data?.content_revision ?? null;
    if (!initialized) return;
    void queryClient.invalidateQueries({
      queryKey: aiBuildKey(draftUuid),
      exact: true,
    });
    if (!refreshRequired) return;
    refreshDraftViews(queryClient, draftUuid, false);
    refreshOperationalViews(queryClient, draftUuid);
  }, [draftUuid, query.data?.content_revision, queryClient, transition]);

  return query;
}

export function useAnswerDropshipV2Clarifications(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const clarificationKey = queryKeys.dropshipV2.clarifications(draftUuid);
  return useMutation({
    mutationFn: async (body: DropshipV2ClarificationAnswersRequest) => {
      const client = await getClient();
      const { data, error, response } = await client.POST(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/clarifications/answers",
        {
          params: { path: { draft_uuid: draftUuid } },
          body,
        },
      );
      if (error) {
        throw new DropshipV2RequestError(
          response.status,
          error,
          "Quote clarification answers were not submitted",
        );
      }
      return data;
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({
        queryKey: clarificationKey,
        exact: true,
      });
      const previous =
        queryClient.getQueryData<DropshipV2ClarificationList>(clarificationKey);
      const answers = new Map(
        body.answers.map((answer) => [answer.question_uuid, answer]),
      );
      queryClient.setQueryData<DropshipV2ClarificationList>(
        clarificationKey,
        (current) =>
          current
            ? {
                ...current,
                questions: current.questions.map((question) => {
                  const answer = answers.get(question.uuid);
                  return answer?.expected_detected_revision ===
                    question.detected_revision
                    ? {
                        ...question,
                        answer: answer.answer,
                        workflow_state: "queued",
                      }
                    : question;
                }),
              }
            : current,
      );
      return { previous };
    },
    onError: (error, _body, context) => {
      if (context?.previous)
        queryClient.setQueryData(clarificationKey, context.previous);
      if (error instanceof DropshipV2RequestError && error.status === 412) {
        void queryClient.refetchQueries({
          queryKey: clarificationKey,
          exact: true,
        });
        void queryClient.invalidateQueries({
          queryKey: aiBuildKey(draftUuid),
          exact: true,
        });
        refreshDraftViews(queryClient, draftUuid);
      }
    },
    onSuccess: (result) => {
      queryClient.setQueryData<DropshipV2ClarificationList>(clarificationKey, {
        draft_uuid: result.draft_uuid,
        content_revision: result.content_revision,
        questions: result.questions,
      });
      void queryClient.invalidateQueries({
        queryKey: clarificationKey,
        exact: true,
      });
      void queryClient.invalidateQueries({
        queryKey: aiBuildKey(draftUuid),
        exact: true,
      });
      refreshDraftViews(queryClient, draftUuid);
      refreshOperationalViews(queryClient, draftUuid);
    },
  });
}

export function useDropshipV2FreightReview(draftUuid: string, enabled = true) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.dropshipV2.freightReview(draftUuid),
    queryFn: async ({ signal }) => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/freight-review",
        { params: { path: { draft_uuid: draftUuid } }, signal },
      );
      if (error) throw error;
      return data;
    },
    enabled: enabled && Boolean(draftUuid),
    refetchInterval: (query) =>
      query.state.data?.groups?.some((group) => group.state === "running")
        ? 1_000
        : false,
  });
}

/** Each manufacturer's arrival if ordered today against the deliver-by date (USA-1329); a live carrier quote, so cached a while. */
export function useDropshipV2DeliveryTiming(draftUuid: string, enabled = true) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.dropshipV2.deliveryTiming(draftUuid),
    queryFn: async ({ signal }) => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/delivery-timing",
        { params: { path: { draft_uuid: draftUuid } }, signal },
      );
      if (error) throw error;
      return data;
    },
    enabled: enabled && Boolean(draftUuid),
    staleTime: 5 * 60_000,
  });
}

export function useRetryDropshipV2FreightReview(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      expectedContentRevision,
    }: {
      expectedContentRevision: number;
    }) => {
      const client = await getClient();
      const { data, error, response } = await client.POST(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/freight-review/retry",
        {
          params: { path: { draft_uuid: draftUuid } },
          body: { expected_content_revision: expectedContentRevision },
        },
      );
      if (error) {
        throw new DropshipV2RequestError(
          response.status,
          error,
          "Freight requote could not start",
        );
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipV2.freightReview(draftUuid),
      });
      refreshOperationalViews(queryClient, draftUuid);
      toast.success("Freight requote started");
    },
    onError: (error) => {
      toast.error("Freight requote could not start", {
        description: errorMessage(error, "Reload the draft and retry."),
      });
    },
  });
}

export function useUpdateDropshipV2Assignments(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      shell,
      assignedSalesUserId,
      assignedPMUserId,
    }: {
      shell: DropshipV2Shell;
      assignedSalesUserId: number | null;
      assignedPMUserId: number | null;
    }) => {
      const client = await getClient();
      const { data, error } = await client.PATCH(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/assignments",
        {
          params: { path: { draft_uuid: draftUuid } },
          body: {
            expected_content_revision: shell.content_revision,
            assigned_sales_user_id: assignedSalesUserId,
            assigned_pm_user_id: assignedPMUserId,
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async ({ shell, assignedSalesUserId, assignedPMUserId }) => {
      const shellKey = queryKeys.dropshipV2.shell(draftUuid);
      await queryClient.cancelQueries({ queryKey: shellKey });
      const previousShell = queryClient.getQueryData<DropshipV2Shell>(shellKey);
      queryClient.setQueryData<DropshipV2Shell>(shellKey, {
        ...(previousShell ?? shell),
        assigned_sales_user_id: assignedSalesUserId,
        assigned_pm_user_id: assignedPMUserId,
      });
      return { previousShell };
    },
    onSuccess: (shell) => {
      queryClient.setQueryData(queryKeys.dropshipV2.shell(draftUuid), shell);
      refreshOperationalViews(queryClient, draftUuid);
      toast.success("Review owners updated");
    },
    onError: (error, _variables, context) => {
      if (context?.previousShell) {
        queryClient.setQueryData(
          queryKeys.dropshipV2.shell(draftUuid),
          context.previousShell,
        );
      }
      toast.error("Review owners were not updated", {
        description: errorMessage(error, "Reload the draft and retry."),
      });
    },
  });
}

export function usePatchDropshipV2Field(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const fieldKey = queryKeys.dropshipV2.fields(draftUuid);
  const issueKey = queryKeys.dropshipV2.issues(draftUuid);
  return useMutation({
    mutationFn: async ({
      field,
      value,
      reapplyOnStale,
    }: {
      field: DropshipV2Field;
      value: unknown;
      reapplyOnStale?: (currentField: DropshipV2Field) => unknown;
    }) => {
      const client = await getClient();
      const patch = (currentField: DropshipV2Field, nextValue: unknown) =>
        client.PATCH(
          "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/fields/{field_uuid}",
          {
            params: {
              path: { draft_uuid: draftUuid, field_uuid: currentField.uuid },
            },
            body: {
              expected_revision: currentField.revision,
              value: nextValue,
            },
          },
        );
      const firstAttempt = await patch(field, value);
      const detail = (firstAttempt.error as V2RequestErrorBody | undefined)
        ?.detail;
      if (
        !firstAttempt.error ||
        firstAttempt.response.status !== 412 ||
        typeof detail !== "object" ||
        detail.code !== "stale_revision" ||
        !reapplyOnStale
      ) {
        if (firstAttempt.error) throw firstAttempt.error;
        return firstAttempt.data;
      }

      const currentFields = await client.GET(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/fields",
        { params: { path: { draft_uuid: draftUuid } } },
      );
      if (currentFields.error) throw currentFields.error;
      queryClient.setQueryData(fieldKey, currentFields.data);
      const currentField = currentFields.data.find(
        (candidate) => candidate.uuid === field.uuid,
      );
      if (!currentField) throw firstAttempt.error;

      const reapplied = await patch(
        currentField,
        reapplyOnStale(currentField),
      );
      if (reapplied.error) throw reapplied.error;
      return reapplied.data;
    },
    onMutate: async ({ field, value }) => {
      await queryClient.cancelQueries({ queryKey: fieldKey });
      const previousField = queryClient
        .getQueryData<DropshipV2Field[]>(fieldKey)
        ?.find((item) => item.uuid === field.uuid);
      queryClient.setQueryData<DropshipV2Field[]>(fieldKey, (current) =>
        current?.map((item) =>
          item.uuid === field.uuid
            ? { ...item, value, revision: item.revision + 1 }
            : item,
        ),
      );
      return { previousField };
    },
    onError: (error, variables, context) => {
      if (context?.previousField) {
        queryClient.setQueryData<DropshipV2Field[]>(fieldKey, (current) =>
          current?.map((item) =>
            item.uuid === variables.field.uuid
              ? (context.previousField as DropshipV2Field)
              : item,
          ),
        );
      }
      toast.error("Edit was not saved", {
        description: errorMessage(error, "The prior value was restored."),
      });
    },
    onSuccess: async (result) => {
      const changedFields = [result.field, ...result.related_fields].filter(
        (field): field is DropshipV2Field => Boolean(field),
      );
      queryClient.setQueryData<DropshipV2Field[]>(fieldKey, (current) =>
        mergeFields(current, changedFields),
      );
      await applyMutationToShell(queryClient, draftUuid, result);
      queryClient.setQueryData<DropshipV2Issue[]>(issueKey, (current) =>
        mergeIssueDelta(current, result.issue_delta),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipV2.clarifications(draftUuid),
      });
      refreshOperationalViews(queryClient, draftUuid);
    },
  });
}

export function useRetryDropshipV2Processing(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/retry-processing",
        { params: { path: { draft_uuid: draftUuid } } },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (result) => {
      if ("line_agents" in result) {
        setAIBuild(queryClient, draftUuid, result);
        void queryClient.invalidateQueries({
          queryKey: queryKeys.dropshipV2.shell(draftUuid),
        });
        return;
      }
      queryClient.setQueryData<DropshipV2Shell>(
        queryKeys.dropshipV2.shell(draftUuid),
        result,
      );
    },
  });
}

export function useDropshipV2Plan(
  draftUuid: string,
  currentPlanId: number | null | undefined,
  enabled = true,
) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.dropshipV2.plan(draftUuid, currentPlanId),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/current-plan",
        { params: { path: { draft_uuid: draftUuid } } },
      );
      if (error) throw error;
      return data;
    },
    enabled: enabled && !!draftUuid && currentPlanId != null,
  });
}

export function useApproveDropshipV2Sales(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      contentRevision,
      note,
      minimumMarginOverrideReason,
    }: {
      contentRevision: number;
      note?: string;
      minimumMarginOverrideReason?: string;
    }) => {
      const client = await getClient();
      const trimmedOverrideReason = minimumMarginOverrideReason?.trim();
      const { data, error } = await client.POST(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/sales-approval",
        {
          params: { path: { draft_uuid: draftUuid } },
          body: {
            expected_content_revision: contentRevision,
            approval_note: note,
            ...(trimmedOverrideReason
              ? { minimum_margin_override: { reason: trimmedOverrideReason } }
              : {}),
          },
        },
      );
      if (error) throw error;
      return data as { approval: unknown; plan: DropshipV2Plan };
    },
    onSuccess: async ({ plan }) => {
      const shellKey = queryKeys.dropshipV2.shell(draftUuid);
      await queryClient.cancelQueries({ queryKey: shellKey, exact: true });
      queryClient.setQueryData(
        queryKeys.dropshipV2.plan(draftUuid, plan.id),
        plan,
      );
      queryClient.setQueryData<DropshipV2Shell>(shellKey, (shell) =>
        shell
          ? {
              ...shell,
              status: "reviewed",
              workflow_state: "pm_review",
              current_po_plan_id: plan.id,
              active_sales_change_request: null,
            }
          : shell,
      );
      void queryClient.invalidateQueries({ queryKey: shellKey });
      refreshOperationalViews(queryClient, draftUuid);
      toast.success("Sales review approved and PO plan generated");
    },
    onError: () => refreshApprovalViews(queryClient, draftUuid),
  });
}

export function useUpdateDropshipV2ProposedPO(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    scope: proposedPOMutationScope(draftUuid),
    mutationFn: async ({
      proposedPO,
      changes,
    }: {
      proposedPO: DropshipV2ProposedPO;
      changes: DropshipV2ProposedPOChanges;
    }) => {
      const client = await getClient();
      const { data, error } = await client.PATCH(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/proposed-pos/{proposed_po_uuid}",
        {
          params: {
            path: { draft_uuid: draftUuid, proposed_po_uuid: proposedPO.uuid },
          },
          body: { ...changes, expected_revision: proposedPO.revision },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async ({ proposedPO, changes }) => {
      const planKey = queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id);
      await queryClient.cancelQueries({ queryKey: planKey });
      const plan = queryClient.getQueryData<DropshipV2Plan>(planKey);
      const previousPO = plan?.proposed_pos.find(
        (candidate) => candidate.uuid === proposedPO.uuid,
      );
      const sourcePO = previousPO ?? proposedPO;
      const invalidatesApproval =
        sourcePO.state === "approved" &&
        Object.keys(changes).some((key) => key !== "change_reason");
      const optimistic: DropshipV2ProposedPO = {
        ...sourcePO,
        ...changes,
        state:
          changes.state ?? (invalidatesApproval ? "ready" : sourcePO.state),
        accessorial_decisions: changes.accessorial_decisions
          ? {
              ...sourcePO.accessorial_decisions,
              ...changes.accessorial_decisions,
            }
          : sourcePO.accessorial_decisions,
        spec_credit_decision: changes.spec_credit_decision
          ? {
              ...sourcePO.spec_credit_decision,
              ...changes.spec_credit_decision,
            }
          : sourcePO.spec_credit_decision,
        revision: sourcePO.revision + 1,
      };
      queryClient.setQueryData<DropshipV2Plan>(planKey, (plan) =>
        updatePlanPO(plan, optimistic),
      );
      const optimisticPO = queryClient
        .getQueryData<DropshipV2Plan>(planKey)
        ?.proposed_pos.find((candidate) => candidate.uuid === proposedPO.uuid);
      return {
        previousPO,
        optimisticPO,
        optimisticRevision: optimistic.revision,
      };
    },
    onError: (error, { proposedPO }, context) => {
      const planKey = queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id);
      queryClient.setQueryData<DropshipV2Plan>(planKey, (plan) =>
        restorePlanPOAtRevision(
          plan,
          context?.previousPO,
          context?.optimisticPO,
          context?.optimisticRevision,
        ),
      );
      toast.error("PO decision was not saved", {
        description: errorMessage(error, "Prior values restored."),
      });
    },
    onSuccess: (proposedPO) => {
      const planKey = queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id);
      queryClient.setQueryData<DropshipV2Plan>(planKey, (plan) =>
        updatePlanPO(plan, proposedPO),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipV2.poPreview(draftUuid, proposedPO.uuid),
      });
      refreshOperationalViews(queryClient, draftUuid);
    },
    onSettled: (_data, _error, { proposedPO }) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id),
        exact: true,
      }),
  });
}

export function useUpdateDropshipV2ProposedPOLineNote(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    scope: proposedPOMutationScope(draftUuid),
    mutationFn: async ({
      proposedPO,
      line,
      internalNote,
    }: {
      proposedPO: DropshipV2ProposedPO;
      line: DropshipV2ProposedPOLine;
      internalNote: string | null;
    }) => {
      const client = await getClient();
      const { data, error } = await client.PATCH(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/proposed-pos/{proposed_po_uuid}/lines/{line_uuid}/internal-note",
        {
          params: {
            path: {
              draft_uuid: draftUuid,
              proposed_po_uuid: proposedPO.uuid,
              line_uuid: line.uuid,
            },
          },
          body: {
            expected_revision: proposedPO.revision,
            internal_note: internalNote,
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async ({ proposedPO, line, internalNote }) => {
      const planKey = queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id);
      await queryClient.cancelQueries({ queryKey: planKey });
      const plan = queryClient.getQueryData<DropshipV2Plan>(planKey);
      const previousPO = plan?.proposed_pos.find(
        (candidate) => candidate.uuid === proposedPO.uuid,
      );
      const sourcePO = previousPO ?? proposedPO;
      const optimistic: DropshipV2ProposedPO = {
        ...sourcePO,
        lines: sourcePO.lines.map((candidate) =>
          candidate.uuid === line.uuid
            ? { ...candidate, internal_note: internalNote }
            : candidate,
        ),
        state: sourcePO.state === "approved" ? "ready" : sourcePO.state,
        revision: sourcePO.revision + 1,
      };
      queryClient.setQueryData<DropshipV2Plan>(planKey, (plan) =>
        updatePlanPO(plan, optimistic),
      );
      const optimisticPO = queryClient
        .getQueryData<DropshipV2Plan>(planKey)
        ?.proposed_pos.find((candidate) => candidate.uuid === proposedPO.uuid);
      return {
        previousPO,
        optimisticPO,
        optimisticRevision: optimistic.revision,
      };
    },
    onError: (error, { proposedPO }, context) => {
      const planKey = queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id);
      queryClient.setQueryData<DropshipV2Plan>(planKey, (plan) =>
        restorePlanPOAtRevision(
          plan,
          context?.previousPO,
          context?.optimisticPO,
          context?.optimisticRevision,
        ),
      );
      toast.error("Internal note was not saved", {
        description: errorMessage(
          error,
          "The last confirmed note was restored.",
        ),
      });
    },
    onSuccess: (proposedPO) => {
      const planKey = queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id);
      queryClient.setQueryData<DropshipV2Plan>(planKey, (plan) =>
        updatePlanPO(plan, proposedPO),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipV2.poPreview(draftUuid, proposedPO.uuid),
      });
      refreshOperationalViews(queryClient, draftUuid);
    },
    onSettled: (_data, _error, { proposedPO }) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id),
        exact: true,
      }),
  });
}

export function useApproveDropshipV2ProposedPO(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    scope: proposedPOMutationScope(draftUuid),
    mutationFn: async ({
      proposedPO,
      note,
      operationsOverrideReason,
    }: {
      proposedPO: DropshipV2ProposedPO;
      note?: string;
      operationsOverrideReason?: string;
    }) => {
      const client = await getClient();
      const trimmedOverrideReason = operationsOverrideReason?.trim();
      const { data, error } = await client.POST(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/proposed-pos/{proposed_po_uuid}/approve",
        {
          params: {
            path: { draft_uuid: draftUuid, proposed_po_uuid: proposedPO.uuid },
          },
          body: {
            expected_revision: proposedPO.revision,
            approval_note: note,
            ...(trimmedOverrideReason
              ? { operations_override: { reason: trimmedOverrideReason } }
              : {}),
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async ({ proposedPO }) => {
      const planKey = queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id);
      await queryClient.cancelQueries({ queryKey: planKey, exact: true });
      const plan = queryClient.getQueryData<DropshipV2Plan>(planKey);
      const previousPO = plan?.proposed_pos.find(
        (candidate) => candidate.uuid === proposedPO.uuid,
      );
      const sourcePO = previousPO ?? proposedPO;
      const optimisticPO: DropshipV2ProposedPO = {
        ...sourcePO,
        state: "approved",
        revision: sourcePO.revision + 1,
      };
      queryClient.setQueryData<DropshipV2Plan>(planKey, (current) =>
        updatePlanPO(current, optimisticPO),
      );
      const cachedOptimisticPO = queryClient
        .getQueryData<DropshipV2Plan>(planKey)
        ?.proposed_pos.find((candidate) => candidate.uuid === proposedPO.uuid);
      return {
        previousPO,
        optimisticPO: cachedOptimisticPO,
        optimisticRevision: optimisticPO.revision,
      };
    },
    onError: (_error, { proposedPO }, context) => {
      const planKey = queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id);
      queryClient.setQueryData<DropshipV2Plan>(planKey, (plan) =>
        restorePlanPOAtRevision(
          plan,
          context?.previousPO,
          context?.optimisticPO,
          context?.optimisticRevision,
        ),
      );
    },
    onSuccess: (proposedPO) => {
      queryClient.setQueryData<DropshipV2Plan>(
        queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id),
        (plan) => updatePlanPO(plan, proposedPO),
      );
      toast.success(`${proposedPO.manufacturer_name} PO approved`);
    },
    onSettled: (_data, _error, { proposedPO }) => {
      refreshApprovalViews(queryClient, draftUuid, proposedPO.plan_id);
      refreshOperationalViews(queryClient, draftUuid);
    },
  });
}

export function useRejectDropshipV2ProposedPOToSales(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    scope: proposedPOMutationScope(draftUuid),
    mutationFn: async ({
      proposedPO,
      reason,
    }: {
      proposedPO: DropshipV2ProposedPO;
      reason: string;
    }) => {
      const client = await getClient();
      const trimmedReason = reason.trim();
      const { data, error } = await client.POST(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/proposed-pos/{proposed_po_uuid}/reject-to-sales",
        {
          params: {
            path: { draft_uuid: draftUuid, proposed_po_uuid: proposedPO.uuid },
          },
          body: {
            expected_revision: proposedPO.revision,
            reason: trimmedReason,
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async ({ proposedPO, reason }) => {
      const shellKey = queryKeys.dropshipV2.shell(draftUuid);
      const planKey = queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id);
      await Promise.all([
        queryClient.cancelQueries({ queryKey: shellKey, exact: true }),
        queryClient.cancelQueries({ queryKey: planKey, exact: true }),
      ]);
      const previousShell = queryClient.getQueryData<DropshipV2Shell>(shellKey);
      const previousPlan = queryClient.getQueryData<DropshipV2Plan>(planKey);
      const optimisticShell = previousShell
        ? {
            ...previousShell,
            status: "needs_review",
            workflow_state: "needs_review",
            current_po_plan_id: null,
            active_sales_change_request: {
              reason: reason.trim(),
              requested_at: new Date().toISOString(),
              proposed_po_uuid: proposedPO.uuid,
              manufacturer_name: proposedPO.manufacturer_name,
            },
          }
        : undefined;
      if (optimisticShell)
        queryClient.setQueryData<DropshipV2Shell>(shellKey, optimisticShell);
      const cachedOptimisticShell =
        queryClient.getQueryData<DropshipV2Shell>(shellKey);
      queryClient.removeQueries({ queryKey: planKey, exact: true });
      return {
        previousShell,
        previousPlan,
        optimisticShell: cachedOptimisticShell,
      };
    },
    onSuccess: (shell, { proposedPO }) => {
      const shellKey = queryKeys.dropshipV2.shell(draftUuid);
      queryClient.setQueryData<DropshipV2Shell>(shellKey, shell);
      toast.success(
        `Quote reopened for the ${proposedPO.manufacturer_name} PO`,
        {
          description:
            "Update the quote and freight, then approve Sales review again.",
        },
      );
    },
    onError: (error, { proposedPO }, context) => {
      const shellKey = queryKeys.dropshipV2.shell(draftUuid);
      const planKey = queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id);
      if (
        context?.previousShell &&
        queryClient.getQueryData(shellKey) === context.optimisticShell
      ) {
        queryClient.setQueryData(shellKey, context.previousShell);
      }
      if (context?.previousPlan && !queryClient.getQueryState(planKey)) {
        queryClient.setQueryData(planKey, context.previousPlan);
      }
      toast.error("Quote was not reopened", {
        description: errorMessage(
          error,
          "The PO remains in PO review. Refresh and try again.",
        ),
      });
    },
    onSettled: (_data, _error, { proposedPO }) => {
      refreshApprovalViews(queryClient, draftUuid, proposedPO.plan_id);
      refreshOperationalViews(queryClient, draftUuid);
    },
  });
}

export function useBulkApproveDropshipV2ProposedPOs(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      proposedPOs,
      note,
    }: {
      proposedPOs: DropshipV2ProposedPO[];
      note?: string;
    }) => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/proposed-pos/bulk-approve",
        {
          params: { path: { draft_uuid: draftUuid } },
          body: {
            items: proposedPOs.map((proposedPO) => ({
              proposed_po_uuid: proposedPO.uuid,
              expected_revision: proposedPO.revision,
            })),
            approval_note: note,
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (approvedPOs) => {
      for (const proposedPO of approvedPOs) {
        queryClient.setQueryData<DropshipV2Plan>(
          queryKeys.dropshipV2.plan(draftUuid, proposedPO.plan_id),
          (plan) => updatePlanPO(plan, proposedPO),
        );
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropshipV2.shell(draftUuid),
      });
      refreshOperationalViews(queryClient, draftUuid);
      toast.success(`${approvedPOs.length} POs approved`);
    },
  });
}

export function usePlaceDropshipV2POs(draftUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      plan,
      proposedPOs,
      releaseSupplierActions = false,
    }: {
      plan: DropshipV2Plan;
      proposedPOs: DropshipV2ProposedPO[];
      releaseSupplierActions?: boolean;
    }) => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/placement",
        {
          params: { path: { draft_uuid: draftUuid } },
          body: {
            plan_version: plan.version,
            items: proposedPOs.map((item) => ({
              proposed_po_uuid: item.uuid,
              expected_revision: item.revision,
            })),
            idempotency_key: idempotencyKey("dropship-placement"),
            release_supplier_actions: releaseSupplierActions,
          },
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (attempt) => {
      queryClient.setQueryData(
        queryKeys.dropshipV2.placement(draftUuid, attempt.uuid),
        attempt,
      );
      refreshOperationalViews(queryClient, draftUuid);
      toast.success("Project start queued");
    },
    onSettled: (_data, _error, { plan }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.dropshipV2.plan(draftUuid, plan.id),
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.dropshipV2.shell(draftUuid),
          exact: true,
        }),
      ]),
  });
}

export function useDropshipV2Placement(
  draftUuid: string,
  attemptUuid?: string,
) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.dropshipV2.placement(draftUuid, attemptUuid ?? ""),
    queryFn: async () => {
      if (!attemptUuid) throw new Error("Missing placement attempt");
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/dropship-intake/v2/drafts/{draft_uuid}/placement/{attempt_uuid}",
        {
          params: {
            path: { draft_uuid: draftUuid, attempt_uuid: attemptUuid },
          },
        },
      );
      if (error) throw error;
      return data;
    },
    enabled: !!attemptUuid,
    refetchInterval: (query) =>
      ["pending", "in_progress"].includes(query.state.data?.status ?? "")
        ? 1_000
        : false,
  });
}

export function useDropshipV2POPreviewBlob(
  draftUuid: string,
  proposedPOUuid: string,
  enabled = true,
) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: queryKeys.dropshipV2.poPreview(draftUuid, proposedPOUuid),
    queryFn: async () => {
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      const response = await fetch(
        `${API_BASE_URL}/dashboard/dropship-intake/v2/drafts/${draftUuid}/proposed-pos/${proposedPOUuid}/preview.pdf`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const body: unknown = await response.json().catch(() => null);
        throw new DropshipV2RequestError(
          response.status,
          body,
          "PO preview failed",
        );
      }
      return response.blob();
    },
    enabled: enabled && Boolean(draftUuid) && Boolean(proposedPOUuid),
  });
}

export { errorMessage as dropshipV2ErrorMessage };
